import { useCallback, useRef, useState } from 'react';
import { JxtClassicPicto } from '@jooxter/ui';
import { BASE_FONT_SIZE, STROKE_WIDTH } from '../constants';
import { IJxtPictoGroup } from '../types';

const PADDING = 8;
const SPACING = 20;
const INTERLINE = 8;
const MAX_CHARACTERS = 40;

export const JxtResourceHeatmapPicto = ({ p, onPictoClick, isHovered, computedRatio }: IJxtPictoGroup) => {
  const showTooltip = !!isHovered;
  const groupRef = useRef<SVGGElement>(null);
  const [titleWidth, setTitleWidth] = useState<number>();
  const [subtitleWidth, setSubtitleWidth] = useState<number>();
  const [subtitleHeight, setsubtitleHeight] = useState<number>();
  const [baseHeight, setBaseHeight] = useState<number>();
  const titleFontSize = computedRatio ? computedRatio * BASE_FONT_SIZE : BASE_FONT_SIZE;
  const baseWidth = titleWidth && (!subtitleWidth || titleWidth > subtitleWidth) ? titleWidth : subtitleWidth;
  const titleRef = useCallback(
    (node: SVGTextElement | null) => {
      if (node) {
        setTitleWidth(node.getBBox().width);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [p]
  );

  const subtitleRef = useCallback(
    (node: SVGTextElement | null) => {
      if (node) {
        setSubtitleWidth(node.getBBox().width);
        setsubtitleHeight(node.getBBox().height);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [p]
  );

  const textRef = useCallback(
    (node: SVGGElement | null) => {
      if (node) {
        setBaseHeight(node.getBBox().height);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [titleWidth, subtitleWidth, subtitleHeight]
  );

  if (!(p.x && p.y)) {
    return null;
  }

  const truncatedTitle =
    p.title && p.title?.length > MAX_CHARACTERS ? p.title.substring(0, MAX_CHARACTERS).concat('...') : p.title;

  const subtitle = p.heatmapIntensity !== undefined ? `${p.heatmapIntensity}%` : undefined;

  return (
    <g
      ref={groupRef}
      data-picto-id={p.id}
      onClick={() => onPictoClick(p.id)}
      onTouchEnd={() => onPictoClick(p.id)}
      className="cursor-pointer"
    >
      <JxtClassicPicto radius={p.size / 2.3} x={p.x} y={p.y} size={p.size} heatmapIntensity={p.heatmapIntensity} />
      {p.title && baseWidth && baseHeight && (
        <rect
          className={showTooltip ? 'visible' : 'invisible'}
          width={baseWidth + PADDING * 2}
          height={baseHeight + PADDING}
          fill="white"
          stroke="#e0e3e8"
          strokeWidth={STROKE_WIDTH}
          rx="8"
          ry="8"
          x={p.x - baseWidth / 2 - 8 + p.size / 2 - 1}
          y={p.y - baseHeight / 2 - PADDING / 2 - (subtitle && subtitleHeight ? SPACING + subtitleHeight / 2 : SPACING)}
        />
      )}
      {p.title && (
        <g ref={textRef}>
          <text
            ref={titleRef}
            className={showTooltip ? 'visible' : 'invisible'}
            textAnchor="middle"
            alignmentBaseline="central"
            x={p.x + p.size / 2}
            y={p.y - (subtitle && subtitleHeight ? SPACING + INTERLINE + subtitleHeight - PADDING : SPACING)}
            fontFamily='"Open Sans", sans-serif'
            fontSize={titleFontSize}
            fontWeight="800"
            fill="#22272e"
          >
            {truncatedTitle}
          </text>
          {subtitle && (
            <text
              ref={subtitleRef}
              className={showTooltip ? 'visible' : 'invisible'}
              textAnchor="middle"
              alignmentBaseline="central"
              x={p.x + p.size / 2}
              y={p.y - SPACING}
              fontFamily='"Open Sans", sans-serif'
              fontSize={titleFontSize}
              fontWeight="400"
              fill="#677489"
            >
              {subtitle}
            </text>
          )}
        </g>
      )}
    </g>
  );
};
